import { useEffect } from "react";
import { pubSub } from "../ably";
import { ADMIN_CHANNELS } from "../channel/channels.js";
import { normalizeQnaList } from "../channel/normalizePayload.js";
import { setAdminQna, getSessionById } from "./adminStore.js";
import { applyKeyedListMessage } from "../realtime/channelSyncEngine.js";

function qnaItemId(item) {
  return String(item?.id);
}

function bankConfig(sessionId) {
  return {
    getId: qnaItemId,
    classify(raw) {
      const list = normalizeQnaList(raw);
      return list && list.length ? { kind: "items", items: list } : { kind: "ignore", items: [] };
    },
    getCurrent: () => getSessionById(sessionId)?.qna || [],
    setResult: (nextList) => setAdminQna(sessionId, nextList),
  };
}

function applyBankItems(cacheKey, sessionId, items) {
  // Ably hands history back newest-first; replay oldest-first so the per-item
  // freshness check inside applyKeyedListMessage only ever moves forward.
  [...items].reverse().forEach((item) => {
    try {
      applyKeyedListMessage({
        cacheKey,
        config: bankConfig(sessionId),
        message: { id: item.id, timestamp: item.timestamp, raw: item.data },
      });
    } catch (e) {
      console.warn("[useAdminQnaSync] history apply failed", e);
    }
  });
}

export default function useAdminQnaSync(sessionId) {
  useEffect(() => {
    if (!sessionId) return;
    const cacheKey = ["admin-qna-bank", Number(sessionId)];

    const handler = (data, _isHistory, meta = {}) => {
      try {
        applyKeyedListMessage({
          cacheKey,
          config: bankConfig(sessionId),
          message: { id: meta.id, timestamp: meta.timestamp, raw: data },
        });
      } catch (e) {
        console.warn("[useAdminQnaSync] failed", e);
      }
    };
    pubSub.subscribe(ADMIN_CHANNELS.ADMIN_QNA, handler);
    return () => pubSub.unsubscribe(ADMIN_CHANNELS.ADMIN_QNA, handler);
  }, [sessionId]);

  // Seed the bank from channel history. The Ably layer replays history exactly
  // once, right after the first connect — a hook mounted later (or a panel that
  // mounts when its tab opens) would otherwise stay empty until the next live
  // publish. Re-reading on mount + reconnect makes the cache deterministic.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const cacheKey = ["admin-qna-bank", Number(sessionId)];

    const seed = async () => {
      try {
        const items = await pubSub.getChannelHistoryWithMeta(ADMIN_CHANNELS.ADMIN_QNA, {
          limit: 100,
          maxPages: 1,
        });
        if (cancelled || !items?.length) return;
        applyBankItems(cacheKey, sessionId, items);
      } catch (e) {
        console.warn("[useAdminQnaSync] history seed failed", e);
      }
    };

    if (pubSub.isConnected) seed();
    const onConn = (connected) => {
      if (connected) seed();
    };
    pubSub.onConnectionChange(onConn);
    return () => {
      cancelled = true;
      pubSub.offConnectionChange(onConn);
    };
  }, [sessionId]);
}
